// Property-list page header — two presentations sharing the same data:
//
//  - Desktop (`md+`): one horizontal row with
//        [title block] [search pill] [view toggle] [Thêm BĐS]
//    The title is at the top of its column and the subtitle below it;
//    items-center on the row pulls the title near the search-pill's
//    vertical center (the comp's intended look).
//
//  - Mobile (`<md`): two stacked sections —
//        row 1: [—————— title block ——————]
//        row 2: [—————— search card ——————]
//    No "Thêm BĐS" button — the mobile shell has a center-bottom FAB
//    for that action, so it would be a duplicate. No view toggle —
//    tables don't fit phone widths, so we lock mobile to the card grid
//    and only render Lưới/Bảng on desktop.
//
// Both presentations live here so the page doesn't track two headers.

import { useNavigate } from "react-router";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/auth-store";
import { canCreateProperty } from "@/features/settings/role-labels";
import { PropertySearchInput } from "@/features/property/components/property-search-input";
import { PropertyViewToggle } from "@/features/property/components/property-view-toggle";
import { NotificationBell } from "@/features/notification/notification-bell";

interface PropertyListHeaderProps {
  total: number;
  statsLine: string;
}

export function PropertyListHeader({ total, statsLine }: PropertyListHeaderProps) {
  return (
    <>
      <MobileHeader total={total} statsLine={statsLine} />
      <DesktopHeader total={total} statsLine={statsLine} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Desktop
// ---------------------------------------------------------------------------

function DesktopHeader({
  total,
  statsLine,
}: PropertyListHeaderProps) {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  return (
    <header className="hidden items-center gap-3 md:flex">
      <TitleBlock total={total} statsLine={statsLine} className="flex-1" />
      <PropertySearchInput variant="desktop" className="min-w-[280px]" />
      <PropertyViewToggle variant="desktop" />
      {canCreateProperty(role) ? (
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate("/properties/new")}
          className="h-[34px] rounded-[10px] px-3.5 text-[12px] font-semibold"
        >
          <Plus aria-hidden="true" className="size-3.5" />
          Thêm BĐS
        </Button>
      ) : null}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Mobile
// ---------------------------------------------------------------------------

function MobileHeader({ total, statsLine }: PropertyListHeaderProps) {
  return (
    <header className="flex flex-col gap-3 md:hidden">
      <div className="flex items-start justify-between gap-3">
        <TitleBlock
          total={total}
          statsLine={statsLine}
          variant="mobile"
          className="min-w-0 flex-1"
        />
        <NotificationBell />
      </div>
      <PropertySearchInput variant="mobile" />
    </header>
  );
}

// ---------------------------------------------------------------------------
// Shared title block
// ---------------------------------------------------------------------------

function TitleBlock({
  total,
  statsLine,
  variant,
  className,
}: PropertyListHeaderProps & {
  variant?: "desktop" | "mobile";
  className?: string;
}) {
  // Mobile uses the larger 26-px / 600-weight treatment from the design
  // comp's mobile dashboard; desktop uses the slightly tighter 22-px /
  // 700-weight treatment to fit alongside the search + toggle row.
  const isMobile = variant === "mobile";
  return (
    <div className={className}>
      <h1
        className={
          isMobile
            ? "flex items-baseline gap-1.5 text-[28px] font-semibold leading-[1.1] tracking-tight"
            : "flex items-baseline gap-1.5 text-[28px] font-bold tracking-tight"
        }
      >
        Bất động sản
        <span
          className={
            isMobile
              ? "font-mono text-[14px] font-medium text-muted-foreground"
              : "font-mono text-[14px] font-medium text-muted-foreground"
          }
        >
          {total}
        </span>
      </h1>
      {statsLine ? (
        <p
          className={
            isMobile
              ? "mt-0.5 font-mono text-[12px] text-muted-foreground"
              : "mt-0.5 text-[12px] text-muted-foreground"
          }
        >
          {statsLine}
        </p>
      ) : null}
    </div>
  );
}
